import React, { useMemo, useState, useCallback } from 'react';
import {
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { invoicePaidAmount } from '../../lib/invoicePaid';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { formatCurrency, deriveInvoiceStatus } from '../../lib/utils';
import { formatCompact } from '../../lib/chartFormatters';

/**
 * Reusable AR Aging Analysis component.
 *
 * Computes aging buckets, summary table, and top overdue clients
 * from an array of invoices. All amounts are converted from native
 * currency to the display currency via cxInv BEFORE aggregation
 * (this is why the figures are only correct once the
 * convert-before-aggregate fix is live).
 *
 * Props:
 *   invoices       - array of invoice objects
 *   cxInv          - (amount, currency) => converted amount
 *   dc             - display currency code ('GBP' | 'EUR')
 *   loading        - boolean, show loading state
 *   topN           - number of top overdue clients to show (default 10)
 */

// Aging bucket definitions (ordered by severity)
const AGING_BUCKETS = [
  { key: 'current', label: 'Current', min: -Infinity, max: 0 },
  { key: '1_30', label: '1-30 days', min: 1, max: 30 },
  { key: '31_60', label: '31-60 days', min: 31, max: 60 },
  { key: '61_90', label: '61-90 days', min: 61, max: 90 },
  { key: '90_plus', label: '90+ days', min: 91, max: Infinity },
];

const AGING_COLORS = {
  current: '#10B981',
  '1_30': '#F59E0B',
  '31_60': '#F97316',
  '61_90': '#EF4444',
  '90_plus': '#991B1B',
};

function getBucket(daysOverdue) {
  for (const b of AGING_BUCKETS) {
    if (daysOverdue >= b.min && daysOverdue <= b.max) return b;
  }
  return AGING_BUCKETS[AGING_BUCKETS.length - 1];
}

function daysBetween(dateStr) {
  if (!dateStr) return null;
  const due = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.floor((now - due) / 86400000);
}

function ARAgingAnalysis(props) {
  const { invoices, cxInv, dc, loading, topN } = props;
  const maxClients = topN ?? 10;

  // Filter to unpaid invoices per deriveInvoiceStatus
  const unpaidInvoices = useMemo(() => {
    if (!invoices) return [];
    // Issued invoices only: BR OS pre-raises rolling bookings' invoices with
    // future issue dates, and a bill not yet sent is not owed (2026-10-25,
    // same rule as the dashboard Cashflow tab).
    const today = new Date().toISOString().slice(0, 10);
    return invoices.filter((inv) => {
      if (inv.issue_date && inv.issue_date.slice(0, 10) > today) return false;
      const status = deriveInvoiceStatus(inv);
      return status !== 'paid' && status !== 'cancelled' && status !== 'void';
    });
  }, [invoices]);

  // Compute aging buckets with converted amounts
  const agingData = useMemo(() => {
    const bucketMap = {};
    for (const b of AGING_BUCKETS) {
      bucketMap[b.key] = { key: b.key, label: b.label, amount: 0, count: 0 };
    }
    let total = 0;

    for (const inv of unpaidInvoices) {
      const days = daysBetween(inv.due_date);
      if (days == null) continue;
      const outstanding = (inv.total_amount ?? 0) - invoicePaidAmount(inv);
      if (outstanding <= 0) continue;
      const converted = cxInv(outstanding, inv.currency);
      const bucket = getBucket(days);
      bucketMap[bucket.key].amount += converted;
      bucketMap[bucket.key].count += 1;
      total += converted;
    }

    return {
      buckets: AGING_BUCKETS.map((b) => bucketMap[b.key]),
      total,
    };
  }, [unpaidInvoices, cxInv]);

  // Compute top overdue clients
  const topOverdueClients = useMemo(() => {
    const clientMap = {};
    const today = new Date().toISOString().slice(0, 10);

    for (const inv of unpaidInvoices) {
      const company = inv.company || '(unknown)';
      if (!clientMap[company]) {
        clientMap[company] = {
          company,
          outstanding: 0,
          overdue: 0,
          invoiceCount: 0,
          oldestDue: null,
          currencies: new Set(),
        };
      }
      const c = clientMap[company];
      const outstanding = (inv.total_amount ?? 0) - invoicePaidAmount(inv);
      if (outstanding <= 0) continue;
      const converted = cxInv(outstanding, inv.currency);
      c.outstanding += converted;
      c.invoiceCount += 1;
      if (inv.currency) c.currencies.add(inv.currency);
      if (inv.due_date && inv.due_date < today) {
        c.overdue += converted;
      }
      if (inv.due_date && (!c.oldestDue || inv.due_date < c.oldestDue)) {
        c.oldestDue = inv.due_date;
      }
    }

    return Object.values(clientMap)
      .filter((c) => c.overdue > 0)
      .sort((a, b) => b.overdue - a.overdue)
      .slice(0, maxClients)
      .map((c) => ({ ...c, currencies: Array.from(c.currencies) }));
  }, [unpaidInvoices, cxInv, maxClients]);

  if (loading && (!invoices || invoices.length === 0)) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Loading aging data...</p>;
  }

  if (!invoices || invoices.length === 0) {
    return <p className="text-sm text-muted-foreground py-8 text-center">No invoice data for aging analysis.</p>;
  }

  return (
    <div className="space-y-4" data-testid="ar-aging-analysis">
      <p className="text-xs text-muted-foreground" data-testid="aging-convert-note">
        Amounts converted from native invoice currency to {dc} before aggregation.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Aging breakdown chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Aging Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {agingData.total > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart
                  layout="vertical"
                  data={agingData.buckets}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    type="number"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(v) => formatCompact(v, dc)}
                  />
                  <YAxis
                    dataKey="label"
                    type="category"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    width={80}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
                          <p className="font-medium mb-1">{label}</p>
                          <p>Amount: {formatCurrency(payload[0].value, dc)}</p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                    {agingData.buckets.map((bucket) => (
                      <Cell key={bucket.key} fill={AGING_COLORS[bucket.key] || '#6B7280'} />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-8 text-center">No outstanding invoices.</p>
            )}
          </CardContent>
        </Card>

        {/* Aging summary table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Aging Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {agingData.total > 0 ? (
              <Table data-testid="aging-summary-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Bucket</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead className="text-right">% of Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agingData.buckets.map((bucket) => {
                    const pct = agingData.total > 0
                      ? ((bucket.amount / agingData.total) * 100).toFixed(1)
                      : '0.0';
                    return (
                      <TableRow key={bucket.key}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-sm shrink-0"
                              style={{ backgroundColor: AGING_COLORS[bucket.key] || '#6B7280' }}
                            />
                            {bucket.label}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(bucket.amount, dc)}
                        </TableCell>
                        <TableCell className="text-right">{bucket.count}</TableCell>
                        <TableCell className="text-right font-mono">{pct}%</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="font-bold border-t-2" data-testid="aging-summary-total">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(agingData.total, dc)}
                    </TableCell>
                    <TableCell className="text-right">
                      {agingData.buckets.reduce((sum, b) => sum + b.count, 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono">100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">No outstanding invoices.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top overdue clients */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top Overdue Clients</CardTitle>
        </CardHeader>
        <CardContent>
          {topOverdueClients.length > 0 ? (
            <div className="overflow-x-auto">
              <Table data-testid="top-overdue-clients-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead className="text-right">Overdue</TableHead>
                    <TableHead className="text-right">Invoices</TableHead>
                    <TableHead>Oldest Due</TableHead>
                    <TableHead>Currencies</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topOverdueClients.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{c.company}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(c.outstanding, dc)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600 dark:text-red-400">
                        {formatCurrency(c.overdue, dc)}
                      </TableCell>
                      <TableCell className="text-right">{c.invoiceCount}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {c.oldestDue || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {c.currencies.map((cur) => (
                            <Badge key={cur} variant="outline" className="text-xs">{cur}</Badge>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">No overdue invoices.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { ARAgingAnalysis };
